import { useCallback, useRef } from "react";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/shallow";

import { storage } from "@/shared/utils/local-storage";
import { useBoundStore } from "@/app/store";
import { FetchedNote } from "@/services/api/fetchers/notes";
import { FetchedTag } from "@/services/api/fetchers/tags";

interface UseNotesHandlersProps {
  selectedNotes: Set<number>;
  setSelectedNotes: (notes: Set<number> | ((prev: Set<number>) => Set<number>)) => void;
  selectedFolders: Set<number>;
  isSelectionMode: boolean;
  setIsSelectionMode: (mode: boolean) => void;
  notes: FetchedNote[] | null;
  updateNote: (id: number, updates: Partial<FetchedNote>, shared: boolean) => void;
  tempDeleteNote: (id: number) => void;
  clearSelection: () => void;
  searchParams: URLSearchParams;
  setSearchParams: (params: URLSearchParams, options?: { replace?: boolean }) => void;
  folderId?: string;
}

export function useNotesHandlers({
  selectedNotes,
  setSelectedNotes,
  selectedFolders,
  isSelectionMode,
  setIsSelectionMode,
  notes,
  updateNote,
  tempDeleteNote,
  clearSelection,
  searchParams,
  setSearchParams,
  folderId,
}: UseNotesHandlersProps) {
  const layoutScrollTop = useRef<number>(null);
  const navigate = useNavigate();
  const deviceType = useBoundStore(useShallow((state) => state.device.type));

  const handleSearch = useCallback(
    (value: string) => {
      const newParams = new URLSearchParams(searchParams);
      if (value) {
        newParams.set("q", value);
      } else {
        newParams.delete("q");
      }
      setSearchParams(newParams, { replace: true });
    },
    [setSearchParams, searchParams]
  );

  const handlePinSelected = useCallback(() => {
    selectedNotes.forEach((noteId) => {
      const note = notes?.find((n) => n.id === noteId);
      if (note) {
        updateNote(noteId, { pinned: !note.pinned }, Boolean(note.public_uuid));
      }
    });
  }, [selectedNotes, notes, updateNote]);

  const handleLockSelected = useCallback(() => {
    selectedNotes.forEach((noteId) => {
      const note = notes?.find((n) => n.id === noteId);
      if (note) {
        updateNote(noteId, { locked: !note.locked }, Boolean(note.public_uuid));
      }
    });
  }, [selectedNotes, notes, updateNote]);

  const handleMaskSelected = useCallback(() => {
    selectedNotes.forEach((noteId) => {
      const note = notes?.find((n) => n.id === noteId);
      if (note) {
        updateNote(noteId, { masked: !note.masked }, Boolean(note.public_uuid));
      }
    });
  }, [selectedNotes, notes, updateNote]);

  const handleDeleteSelected = useCallback(() => {
    // Delete selected notes
    selectedNotes.forEach((noteId) => {
      tempDeleteNote(noteId);
    });

    // Clear selection after deletion
    clearSelection();
  }, [selectedNotes, tempDeleteNote, clearSelection]);

  const handleNoteSelect = useCallback(
    (noteId: number) => {
      const wasSelected = selectedNotes.has(noteId);
      const newNotesSize = wasSelected ? selectedNotes.size - 1 : selectedNotes.size + 1;

      setSelectedNotes((prev) => {
        const newSelected = new Set(prev);
        if (newSelected.has(noteId)) {
          newSelected.delete(noteId);
        } else {
          newSelected.add(noteId);
        }
        return newSelected;
      });

      // Exit selection mode if no notes are selected and no folders are selected
      if (newNotesSize === 0 && selectedFolders.size === 0) {
        setIsSelectionMode(false);
      } else if (!isSelectionMode) {
        setIsSelectionMode(true);
      }
    },
    [isSelectionMode, selectedNotes, selectedFolders.size, setSelectedNotes, setIsSelectionMode]
  );

  const handleLongPress = useCallback(
    (noteId: number) => {
      if (deviceType === "mobile") {
        handleNoteSelect(noteId);
      }
    },
    [deviceType, handleNoteSelect]
  );

  const handlePress = useCallback(
    (id: number, wasKeyboard?: boolean, ctrlKey?: boolean, longPress?: boolean) => {
      // Handle long press on mobile
      if (longPress) {
        handleLongPress(id);
        return;
      }

      // Handle Ctrl+click on desktop for selection
      if (ctrlKey && deviceType === "desktop") {
        handleNoteSelect(id);
        return;
      }

      // Handle selection mode clicks
      if (isSelectionMode) {
        handleNoteSelect(id);
        return;
      }

      // Normal navigation
      storage.setNotesNavigationState({
        scrollPosition: layoutScrollTop.current ?? 0,
        wasKeyboard: wasKeyboard ?? false,
      });
      const folderParam = folderId || "0";
      navigate(`/notes/${folderParam}/${id}`);
    },
    [handleLongPress, deviceType, handleNoteSelect, isSelectionMode, navigate, folderId]
  );

  const handleNewNote = useCallback(() => {
    const folderParam = folderId || "0";
    navigate(`/notes/${folderParam}/create`);
  }, [navigate, folderId]);

  const handleTagsChange = useCallback(
    (newTags: FetchedTag[]) => {
      // Update local storage
      storage.setNotesSearch({ text: searchParams.get("q") || "", tags: newTags });
    },
    [searchParams]
  );

  return {
    layoutScrollTop,
    handleSearch,
    handlePinSelected,
    handleLockSelected,
    handleMaskSelected,
    handleDeleteSelected,
    handleNoteSelect,
    handleLongPress,
    handlePress,
    handleNewNote,
    handleTagsChange,
  };
}
